import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faSave, 
  faTimes, 
  faSpinner,
  faUserShield,
  faUserCog,
  faUser,
  faCopy,
  faTrash // Added
} from '@fortawesome/free-solid-svg-icons';
import { logger } from '../../utils/logger';
import '../../styles/UserManagementDashboard.css';
import { 
  updateUserRole,
  updateMembershipStatus,
  getAllUsers,
  requestDeleteUserAccount // Added
} from '../../services/userService';
import { UserProfile, UserRole, MembershipStatus } from '../../types/user';
import ListToolbar from '../common/ListToolbar';
import ConfirmDialog from '../common/ConfirmDialog'; // Added

/**
 * User management dashboard component for administrators
 */
const UserManagementDashboard: React.FC = () => {
  // We'll use analytics tracking later when implementing admin actions
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true); // For general loading like fetching all users
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>('user');
  const [selectedStatus, setSelectedStatus] = useState<MembershipStatus>('active');
  const [error, setError] = useState<string>(''); // For general errors
  const [success, setSuccess] = useState<string>(''); // For general success messages
  const [copiedUid, setCopiedUid] = useState<string | null>(null);
  
  // New state for ListToolbar
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<string>('all');
  const [sort, setSort] = useState<string>('newest');

  // State for admin deletion
  const [showAdminDeleteConfirm, setShowAdminDeleteConfirm] = useState<boolean>(false); // Added
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null); // Added
  const [deleteReason, setDeleteReason] = useState<string>(''); // Added
  const [adminDeleteError, setAdminDeleteError] = useState<string>(''); // Added
  const [isDeletingUser, setIsDeletingUser] = useState<boolean>(false); // Added
  
  // Fetch all users on component mount
  useEffect(() => {
    fetchAllUsers();
  }, []);
  
  // This effect clears success/error messages after 5 seconds
  useEffect(() => {
    if (error || success || adminDeleteError) {
      const timerId = setTimeout(() => {
        setError('');
        setSuccess('');
        setAdminDeleteError('');
      }, 5000);
      return () => clearTimeout(timerId);
    }
  }, [error, success, adminDeleteError]);
  
  // This effect clears the copied UID indicator after 2 seconds
  useEffect(() => {
    if (copiedUid) {
      const timerId = setTimeout(() => {
        setCopiedUid(null);
      }, 2000);
      return () => clearTimeout(timerId);
    }
  }, [copiedUid]);
  
  const fetchAllUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const allUsers = await getAllUsers();
      setUsers(allUsers);
      // Keep success message general if needed, or remove if fetchAllUsers shouldn't show one
      // setSuccess(`Loaded ${allUsers.length} users`); 
      logger.success('✅ All users loaded successfully', { count: allUsers.length });
    } catch (err) {
      logger.error('❌ Failed to load users:', err);
      setError('An error occurred while loading users. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyUid = (uid: string) => {
    navigator.clipboard.writeText(uid)
      .then(() => {
        setCopiedUid(uid);
        logger.info('✅ Copied UID to clipboard', { uid });
      })
      .catch(err => {
        logger.error('❌ Failed to copy UID to clipboard', err);
        setError('Failed to copy UID to clipboard');
      });
  };
  
  const handleEdit = (uid: string, currentRole: UserRole, currentStatus: MembershipStatus) => {
    setEditingUser(uid);
    setSelectedRole(currentRole);
    setSelectedStatus(currentStatus);
    setAdminDeleteError(''); // Clear delete error when starting edit
  };
  
  const handleCancelEdit = () => {
    setEditingUser(null);
  };
  
  const handleSave = async (uid: string) => {
    // Use a specific loading state for saving if needed, or reuse global 'loading' if acceptable
    // For now, using global 'loading' as per original structure
    setLoading(true); 
    setError('');
    setSuccess('');
    setAdminDeleteError('');
    
    try {
      const user = users.find(u => u.uid === uid);
      if (!user) throw new Error('User not found');
      
      if (user.role !== selectedRole) {
        await updateUserRole(uid, selectedRole);
        logger.success(`✅ Updated user role for ${user.username} to ${selectedRole}`);
      }
      
      if (user.membershipStatus !== selectedStatus) {
        await updateMembershipStatus(uid, selectedStatus);
        logger.success(`✅ Updated membership status for ${user.username} to ${selectedStatus}`);
      }
      
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.uid === uid 
            ? { ...u, role: selectedRole, membershipStatus: selectedStatus, updatedAt: Date.now() } // Assuming updatedAt for sorting
            : u
        )
      );
      
      setSuccess(`User ${user.username || uid} has been updated successfully.`);
      setEditingUser(null);
    } catch (err: any) {
      logger.error('❌ Failed to update user:', err);
      setError(err.message || 'An error occurred while updating the user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteUser = (user: UserProfile) => {
    setUserToDelete(user);
    setDeleteReason('');
    setAdminDeleteError('');
    setShowAdminDeleteConfirm(true);
  };

  // Confirm and execute user deletion by admin
  const handleAdminDeleteUserConfirm = async () => {
    if (!userToDelete) {
      setAdminDeleteError("User to delete not found. Please try again.");
      setShowAdminDeleteConfirm(false);
      return;
    }

    if (deleteReason.length > 500) {
      setAdminDeleteError("Reason cannot exceed 500 characters.");
      return; // Keep dialog open for correction
    }

    setIsDeletingUser(true);
    setAdminDeleteError(''); // Clear previous error

    try {
      logger.info(`🚀 Admin requesting account deletion for UID: ${userToDelete.uid}`, { reason: deleteReason });
      await requestDeleteUserAccount({ uid: userToDelete.uid, reason: deleteReason });
      
      setUsers(prevUsers => prevUsers.filter(u => u.uid !== userToDelete.uid));
      setSuccess(`User ${userToDelete.username || userToDelete.uid} deleted successfully.`);
      logger.success(`✅ Account deletion request successful for UID: ${userToDelete.uid}`);
      
    } catch (err: any) {
      logger.error(`❌ Error deleting account for UID: ${userToDelete.uid} by admin`, err);
      setAdminDeleteError(err.message || 'Failed to delete user. Please try again.');
    } finally {
      setIsDeletingUser(false);
      setShowAdminDeleteConfirm(false);
      setUserToDelete(null);
      setDeleteReason('');
    }
  };
  
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(e.target.value as UserRole);
  };
  
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value as MembershipStatus);
  };
  
  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'admin': return <FontAwesomeIcon icon={faUserShield} />;
      case 'moderator': return <FontAwesomeIcon icon={faUserCog} />;
      default: return <FontAwesomeIcon icon={faUser} />;
    }
  };
  
  const filteredUsers = users
    .filter(user => {
      if (filter === 'admin') return user.role === 'admin';
      if (filter === 'moderator') return user.role === 'moderator';
      if (filter === 'user') return user.role === 'user';
      if (filter === 'active') return user.membershipStatus === 'active';
      if (filter === 'suspended') return user.membershipStatus === 'suspended';
      if (filter === 'pending') return user.membershipStatus === 'pending';
      if (filter === 'inactive') return user.membershipStatus === 'inactive';
      return true;
    })
    .filter(user => {
      if (!search) return true;
      const searchLower = search.toLowerCase();      return (
        (user.username && user.username.toLowerCase().includes(searchLower)) ||
        (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
        (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
        (user.email && user.email.toLowerCase().includes(searchLower)) ||
        (user.uid && user.uid.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      const aTimestamp = a.updatedAt || a.createdAt;
      const bTimestamp = b.updatedAt || b.createdAt;
      if (sort === 'username') return (a.username || '').localeCompare(b.username || '');
      if (sort === 'realName') return ((a.firstName || '') + ' ' + (a.lastName || '')).localeCompare((b.firstName || '') + ' ' + (b.lastName || ''));
      if (sort === 'oldest') return aTimestamp - bTimestamp;
      return bTimestamp - aTimestamp; // Default: newest (most recently created or updated)
    });

  // Message for ConfirmDialog including textarea
  const dialogMessage = userToDelete ? (
    <>
      Are you sure you want to permanently delete user <strong>{userToDelete.username || userToDelete.uid}</strong>
      {userToDelete.email ? ` (${userToDelete.email})` : ''}?
      This action cannot be undone, and all associated data will be removed.
      <br /><br />      <label htmlFor='deleteReasonAdmin' className="usm-delete-label">
        Reason for deletion (optional, max 500 characters):
      </label>
      <textarea
        id='deleteReasonAdmin'
        value={deleteReason}
        onChange={(e) => setDeleteReason(e.target.value)}
        maxLength={500}
        className="usm-delete-textarea"
        disabled={isDeletingUser}
        placeholder="Enter reason for deletion (e.g., ToS violation, user request)..."
      />
      {deleteReason.length > 450 && (
         <small className={`usm-character-count ${deleteReason.length > 500 ? 'usm-over-limit' : ''}`}>
           {deleteReason.length}/500
         </small>
       )}
    </>
  ) : "";
  
  return (
    <div className="usm-container">
      <h1 className="usm-title">User Management Dashboard</h1>
        <div className="usm-toolbar-container">
        <ListToolbar
          filterOptions={[
            { label: 'All Users', value: 'all' },
            { label: 'Admins', value: 'admin' },
            { label: 'Moderators', value: 'moderator' },
            { label: 'Regular Users', value: 'user' },
            { label: 'Active', value: 'active' },
            { label: 'Suspended', value: 'suspended' },
            { label: 'Pending', value: 'pending' },
            { label: 'Inactive', value: 'inactive' },
          ]}
          filter={filter}
          setFilter={setFilter}
          search={search}
          setSearch={setSearch}
          sortOptions={[
            { label: 'Newest First', value: 'newest' },
            { label: 'Oldest First', value: 'oldest' },
            { label: 'Username (A-Z)', value: 'username' },
            { label: 'Real Name (A-Z)', value: 'realName' },
          ]}
          sort={sort}
          setSort={setSort}
        />
      </div>
      
      {loading && !isDeletingUser && ( // Show general loading only if not specifically deleting
        <div className="usm-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading users...</p>
        </div>
      )}
      
      {/* Display general errors and admin delete errors separately or combined */}
      {error && <div className="usm-error">{error}</div>}
      {adminDeleteError && <div className="usm-error usm-delete-error">{adminDeleteError}</div>}
      {success && <div className="usm-success">{success}</div>}
      
      {!loading && filteredUsers.length > 0 && (
        <div className="usm-users-section">
          <div className="usm-users-table">
            <table className="usm-table">
              <thead>
                <tr>
                  <th className="usm-mobile-show">Avatar</th>
                  <th>Username</th>
                  <th className="usm-mobile-hide">Email</th>
                  <th className="usm-mobile-hide">Real Name</th>
                  <th>UID</th>
                  <th>Role</th>
                  <th className="usm-mobile-hide">Membership</th>
                  <th className="usm-mobile-hide">Last Update/Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.uid} className={isDeletingUser && userToDelete?.uid === user.uid ? 'usm-row-deleting' : ''}>
                    <td className="usm-avatar-cell usm-mobile-show">
                      <div className="usm-avatar">
                        {user.photoURL || user.profilePictureUrl ? (
                          <img 
                            src={user.photoURL || user.profilePictureUrl} 
                            alt={`${user.username}'s avatar`} 
                            className="usm-avatar-img"
                          />
                        ) : (
                          <div className="usm-avatar-placeholder">
                            {user.username ? user.username.charAt(0).toUpperCase() : (user.email ? user.email.charAt(0).toUpperCase() : 'U')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{user.username || '-'}</td>
                    <td className="usm-mobile-hide">{user.email || '-'}</td>
                    <td className="usm-mobile-hide">{(user.firstName || '') + (user.lastName ? ' ' + user.lastName : '') || '-'}</td>
                    <td className="usm-uid-cell">
                      <div className="usm-uid-container">
                        <span className="usm-uid-text" title={user.uid}>{user.uid.substring(0, 8)}...</span>
                        <button 
                          className="usm-copy-button" 
                          onClick={() => handleCopyUid(user.uid)}
                          title="Copy UID to clipboard"
                          disabled={isDeletingUser && userToDelete?.uid === user.uid}
                        >
                          <FontAwesomeIcon 
                            icon={faCopy} 
                            className={copiedUid === user.uid ? 'usm-copied' : ''}
                          />
                          <span className="usm-copy-tooltip">
                            {copiedUid === user.uid ? 'Copied!' : 'Copy'}
                          </span>
                        </button>
                      </div>
                    </td>
                    <td>
                      {editingUser === user.uid ? (                        <select 
                          className="usm-select"
                          value={selectedRole}
                          onChange={handleRoleChange}
                          disabled={loading || (isDeletingUser && userToDelete?.uid === user.uid)}
                          title="Select user role"
                          aria-label="Select user role"
                        >
                          <option value="user">User</option>
                          <option value="moderator">Moderator</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span>
                          {getRoleIcon(user.role)} <span className="usm-mobile-hide">{user.role.charAt(0).toUpperCase() + user.role.slice(1)}</span>
                        </span>
                      )}
                    </td>
                    <td className="usm-mobile-hide">
                      {editingUser === user.uid ? (                        <select 
                          className="usm-select"
                          value={selectedStatus}
                          onChange={handleStatusChange}
                          disabled={loading || (isDeletingUser && userToDelete?.uid === user.uid)}
                          title="Select membership status"
                          aria-label="Select membership status"
                        >
                          <option value="active">Active</option>
                          <option value="pending">Pending</option>
                          <option value="suspended">Suspended</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      ) : (
                        <span className={`status-${user.membershipStatus?.toLowerCase()}`}>
                          {user.membershipStatus ? (user.membershipStatus.charAt(0).toUpperCase() + user.membershipStatus.slice(1)) : 'N/A'}
                        </span>
                      )}
                    </td>
                    <td className="usm-mobile-hide">
                      {new Date(user.updatedAt || user.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="usm-actions">
                        {editingUser === user.uid ? (
                          <>
                            <button 
                              className="usm-button usm-button-save"
                              onClick={() => handleSave(user.uid)}
                              disabled={loading || (isDeletingUser && userToDelete?.uid === user.uid)}
                              title="Save Changes"
                            >
                              <FontAwesomeIcon icon={loading ? faSpinner : faSave} spin={loading && editingUser === user.uid && !isDeletingUser} />
                              <span className="usm-mobile-hide">Save</span>
                            </button>
                            <button 
                              className="usm-button usm-button-cancel"
                              onClick={handleCancelEdit}
                              disabled={loading || (isDeletingUser && userToDelete?.uid === user.uid)}
                              title="Cancel Edit"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                              <span className="usm-mobile-hide">Cancel</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="usm-button usm-button-edit"
                              onClick={() => handleEdit(user.uid, user.role, user.membershipStatus || 'active')}
                              disabled={isDeletingUser && userToDelete?.uid === user.uid}
                              title="Edit User"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                              <span className="usm-mobile-hide">Edit</span>
                            </button>
                            <button
                              className="usm-button usm-button-delete"
                              onClick={() => handleDeleteUser(user)}
                              disabled={isDeletingUser && userToDelete?.uid === user.uid}
                              title="Delete User"
                            >
                              <FontAwesomeIcon icon={isDeletingUser && userToDelete?.uid === user.uid ? faSpinner : faTrash} spin={isDeletingUser && userToDelete?.uid === user.uid} />
                              <span className="usm-mobile-hide">Delete</span>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {!loading && filteredUsers.length === 0 && (
        <div className="usm-empty-state">
          <p>No users found matching the current filters.</p>
          <p>Try changing your search criteria or filters.</p>
        </div>
      )}

      {showAdminDeleteConfirm && userToDelete && (
        <ConfirmDialog
          title="Delete User Account?"
          message={dialogMessage}
          confirmLabel={isDeletingUser ? "Deleting..." : "Delete User"}
          cancelLabel="Cancel"
          isDestructive={true}
          onConfirm={handleAdminDeleteUserConfirm}
          onCancel={() => { 
            setShowAdminDeleteConfirm(false); 
            setUserToDelete(null); 
            setDeleteReason(''); 
            setAdminDeleteError('');
          }}
          isLoading={isDeletingUser}
        />
      )}
    </div>
  );
}

export default UserManagementDashboard;
